import { UseFormReturn, useWatch } from "react-hook-form";
import { WizardSchemaType } from "../index";
import React, { useMemo } from "react";
import { AccordionDetails, Button, Stack } from "@mui/material";
import TextMaxLine from "@onair/ui/text-max-line";
import { Iconify } from "@onair/minimal-ui/iconify";
import EventSessionNumberBadge from "./event-session-number-badge";
import EventSessionPricingForm from "./event-session-pricing-form";
import {
	EventSessionAccordion,
	EventSessionAccordionSummary,
} from "./event-session-accordion";
import has from "lodash/has";
import { EventSessionPricingSchema } from "../step-timeline";
import omit from "lodash/omit";
import { LocationType } from "@onair/repositories";

interface Props {
	index: number;
	methods: UseFormReturn<WizardSchemaType>;
}

const EventSessionPricingItem = ({ index, methods }: Props) => {
	const { control, getFieldState } = methods;

	const locationType = useWatch({
		name: `locationType`,
		control,
	});

	const isOffline = locationType === LocationType.OFFLINE;

	const { error } = getFieldState(`stepPricing.sessions.${index}.ticketTiers.0`);

	const isError = useMemo(() => {
		if (isOffline) return !!error;

		const fields = EventSessionPricingSchema.keyof().options.filter(
			(field) => !["roomProvider", "roomUrl", "roomInfo"].includes(field),
		);

		return fields.some((field) => has(error, field));
	}, [error, isOffline]);

	const eventSessionName = useWatch({
		name: `stepTimeline.sessions.${index}.name`,
		control,
	});

	const header = useMemo(
		() => (
			<Stack
				direction={{
					xs: "column",
					md: "row",
				}}
				alignItems={{
					xs: "flex-start",
					md: "center",
				}}
				spacing={1}
			>
				<EventSessionNumberBadge number={index + 1} />
				<TextMaxLine
					line={1}
					variant="subtitle2"
					lineHeight="20px"
					fontWeight={600}
				>
					{eventSessionName || "Tên sự kiện"}
				</TextMaxLine>
			</Stack>
		),
		[eventSessionName, index],
	);

	return (
		<EventSessionAccordion>
			<EventSessionAccordionSummary
				// @ts-ignore
				isError={isError}
				expandIcon={
					<Button
						variant="soft"
						sx={{
							backgroundColor: "white",
							width: 34,
							minWidth: "unset",
						}}
					>
						<Iconify
							color="action.active"
							width={18}
							icon="eva:arrow-ios-downward-fill"
						/>
					</Button>
				}
			>
				<Stack
					width="100%"
					direction="row"
					justifyContent="space-between"
					alignItems={{
						xs: "flex-start",
						md: "center",
					}}
				>
					<Stack
						direction={{
							xs: "column",
							md: "row",
						}}
						alignItems={{
							xs: "flex-start",
							md: "end",
						}}
						justifyContent={{
							xs: "flex-start",
							md: "space-between",
						}}
						spacing={1}
					>
						{header}
					</Stack>
				</Stack>
			</EventSessionAccordionSummary>
			<AccordionDetails
				sx={{
					pt: {
						xs: 0,
						md: 2,
					},
				}}
			>
				<EventSessionPricingForm index={index} methods={methods} />
			</AccordionDetails>
		</EventSessionAccordion>
	);
};

export default EventSessionPricingItem;
